import { apiFetch, type ApiFetchOptions } from '~/utils/apiFetch'
import { createUuid } from '~/utils/uuid'
import type {
  CurrencyBalanceDto,
  WithdrawableDto,
  DepositCatalogDto,
  DepositAddressDto,
  CreateCryptoDepositOrderDto,
  CryptoDepositOrderDto,
  CreateCryptoWithdrawOrderDto,
  CryptoWithdrawOrderDto,
  CreateFiatWithdrawOrderDto,
  FiatWithdrawOrderDto,
  WithdrawOrderListDto,
  WithdrawOrderListQuery,
  WithdrawOrderItemDto,
  BankDepositOrderDto,
  CashierDepositOrderDto,
  EmbeddedBalanceDto,
  CreateEmbeddedDepositOrderDto,
  EmbeddedDepositOrderDto,
  DepositOrderItemDto,
  DepositOrderListDto,
  DepositOrderListQuery,
  WithdrawCatalogDto,
  ListFiatPayoutAccountsDto,
  CreateFiatPayoutAccountDto,
  UpdateFiatPayoutAccountDto,
  FiatPayoutAccountDto,
  ListFiatPayoutBankCodesDto,
  ListFiatPayoutEwalletProvidersDto,
  ListFiatPayoutCountriesDto,
  ListCryptoPayoutAddressesDto,
  CreateCryptoPayoutAddressDto,
  UpdateCryptoPayoutAddressDto,
  CryptoPayoutAddressDto,
  TransferredResponseDto,
} from '~/types/api/wallet'
import type {
  RecoverAllVenuesResponseDto,
  UpdateVenueSettingsDto,
  VenueListDto,
  VenueSettingsDto,
  VenueTransferRequestDto,
  VenueTransferResponseDto,
} from '~/types/api/venues'

const authBody = (key: 'fund_password' | 'verification_id', value?: string) => {
  const v = value?.trim()
  return v ? { [key]: v } : undefined
}

/** GET /api/v1/wallet/balances — 钱包 USDT 余额 */
export function fetchWalletBalances() {
  return apiFetch<CurrencyBalanceDto>('/wallet/balances')
}

/** GET /api/v1/wallet/withdrawable — 可提现余额（提现页专用） */
export function fetchWithdrawable() {
  return apiFetch<WithdrawableDto>('/wallet/withdrawable')
}

/** GET /api/v1/wallet/deposit/catalog — 充值类型与渠道目录 */
export function fetchDepositCatalog() {
  return apiFetch<DepositCatalogDto>('/wallet/deposit/catalog')
}

/** GET /api/v1/wallet/deposit/address — 充币地址 */
export function fetchDepositAddress(chainCode: string, assetSymbol: string) {
  return apiFetch<DepositAddressDto>('/wallet/deposit/address', {
    query: { chain_code: chainCode, asset_symbol: assetSymbol },
  })
}

/** POST /api/v1/wallet/crypto/deposit-orders — 官方crypto创建充值订单 */
export function createCryptoDepositOrder(payload: CreateCryptoDepositOrderDto) {
  return apiFetch<CryptoDepositOrderDto>('/wallet/crypto/deposit-orders', {
    method: 'POST',
    body: payload,
  })
}

/** POST /api/v1/wallet/bank/deposit-orders — 银行卡转账获取收款账户（建单） */
export function createBankDepositOrder(payload: {
  amount: string
  channel_code: string
  client_order_id: string
}) {
  return apiFetch<BankDepositOrderDto>('/wallet/bank/deposit-orders', {
    method: 'POST',
    body: payload,
  })
}

/** POST /api/v1/wallet/cashier/deposit-orders — 收银台支付创建充值订单 */
export function createCashierDepositOrder(payload: {
  amount: string
  channel_code: string
  client_order_id: string
}) {
  return apiFetch<CashierDepositOrderDto>('/wallet/cashier/deposit-orders', {
    method: 'POST',
    body: payload,
  })
}

/**
 * POST /api/v1/wallet/embedded/balance — 内嵌钱包余额查询
 * 平台代玩家登录钱包；phone 必须走 JSON body（禁止 URL query，会被 access log 记录）。
 * balance 为字符串，调用方勿转 number。
 */
export function fetchEmbeddedBalance(walletCode: string, phone: string) {
  return apiFetch<EmbeddedBalanceDto>('/wallet/embedded/balance', {
    method: 'POST',
    body: { wallet_code: walletCode, phone },
  })
}

/**
 * POST /api/v1/wallet/embedded/deposit-orders — 内嵌钱包划转充值
 * 同步返回，成功即 paid；无回调、无查单接口，调用方禁止自动重试。
 */
export function createEmbeddedDepositOrder(payload: CreateEmbeddedDepositOrderDto) {
  return apiFetch<EmbeddedDepositOrderDto>('/wallet/embedded/deposit-orders', {
    method: 'POST',
    body: payload,
  })
}

/** POST /api/v1/wallet/crypto/withdraw-orders — 创建加密提现订单 */
export function createCryptoWithdrawOrder(payload: CreateCryptoWithdrawOrderDto) {
  return apiFetch<CryptoWithdrawOrderDto>('/wallet/crypto/withdraw-orders', {
    method: 'POST',
    body: payload,
  })
}

/** POST /api/v1/wallet/fiat/withdraw-orders — 创建法币提现订单（数字钱包 / 银行卡 / 支付宝） */
export function createFiatWithdrawOrder(payload: CreateFiatWithdrawOrderDto) {
  return apiFetch<FiatWithdrawOrderDto>('/wallet/fiat/withdraw-orders', {
    method: 'POST',
    body: payload,
  })
}

/** POST /api/v1/wallet/deposit-orders/{id}/cancel — 取消充值订单 */
export function cancelDepositOrder(id: string) {
  return apiFetch(`/wallet/deposit-orders/${id}/cancel`, {
    method: 'POST',
  })
}

/** GET /api/v1/wallet/deposit-orders — 充值订单列表 */
export function fetchDepositOrders(query?: DepositOrderListQuery) {
  return apiFetch<DepositOrderListDto>('/wallet/deposit-orders', { query })
}

/** POST /api/v1/wallet/embedded/deposit-orders/{order_id}/pay — VIPPAY 确认支付 */
export function payEmbeddedDepositOrder(orderId: string, securePassword: string) {
  return apiFetch<TransferredResponseDto>(`/wallet/embedded/deposit-orders/${orderId}/pay`, {
    method: 'POST',
    body: { secure_password: securePassword },
  })
}

/** POST /api/v1/wallet/deposit-orders/{id}/transferred — 标记已转账/支付 */
export function markDepositTransferred(id: string) {
  return apiFetch<TransferredResponseDto>(`/wallet/deposit-orders/${id}/transferred`, {
    method: 'POST',
  })
}

/** GET /api/v1/wallet/deposit-orders/{id} — 充值订单详情 */
export function fetchDepositOrder(id: string, options?: ApiFetchOptions) {
  return apiFetch<DepositOrderItemDto>(`/wallet/deposit-orders/${id}`, options)
}

/** GET /api/v1/wallet/withdraw-orders — 提现订单列表 */
export function fetchWithdrawOrders(query?: WithdrawOrderListQuery) {
  return apiFetch<WithdrawOrderListDto>('/wallet/withdraw-orders', { query })
}

/** GET /api/v1/wallet/withdraw-orders/{order_id} — 提现订单详情 */
export function fetchWithdrawOrder(orderId: string) {
  return apiFetch<WithdrawOrderItemDto>(`/wallet/withdraw-orders/${encodeURIComponent(orderId)}`)
}

/** POST /api/v1/wallet/withdraw-orders/{order_id}/cancel — 取消提现订单 */
export function cancelWithdrawOrder(orderId: string, fundPassword?: string) {
  return apiFetch(`/wallet/withdraw-orders/${encodeURIComponent(orderId)}/cancel`, {
    method: 'POST',
    body: authBody('fund_password', fundPassword),
  })
}

/** GET /api/v1/wallet/withdraw/catalog — 提现方式目录 */
export function fetchWithdrawCatalog() {
  return apiFetch<WithdrawCatalogDto>('/wallet/withdraw/catalog')
}

/** GET /api/v1/wallet/payout-accounts/fiat — 法币收款账户列表 */
export function fetchFiatPayoutAccounts() {
  return apiFetch<ListFiatPayoutAccountsDto>('/wallet/payout-accounts/fiat')
}

/** POST /api/v1/wallet/payout-accounts/fiat — 新增法币收款账户 */
export function createFiatPayoutAccount(payload: CreateFiatPayoutAccountDto) {
  return apiFetch<FiatPayoutAccountDto>('/wallet/payout-accounts/fiat', {
    method: 'POST',
    body: payload,
  })
}

/** PATCH /api/v1/wallet/payout-accounts/fiat/{id} — 编辑法币收款账户 */
export function updateFiatPayoutAccount(id: number, payload: UpdateFiatPayoutAccountDto) {
  return apiFetch<FiatPayoutAccountDto>(`/wallet/payout-accounts/fiat/${id}`, {
    method: 'PATCH',
    body: payload,
  })
}

/** DELETE /api/v1/wallet/payout-accounts/fiat/{id} */
export function deleteFiatPayoutAccount(id: number, fundPassword?: string) {
  return apiFetch(`/wallet/payout-accounts/fiat/${id}`, {
    method: 'DELETE',
    body: authBody('fund_password', fundPassword),
  })
}

/** POST /api/v1/wallet/payout-accounts/fiat/{id}/default */
export function setDefaultFiatPayoutAccount(id: number, fundPassword?: string) {
  return apiFetch(`/wallet/payout-accounts/fiat/${id}/default`, {
    method: 'POST',
    body: authBody('fund_password', fundPassword),
  })
}

/** GET /api/v1/wallet/payout-accounts/fiat/bank-codes — 银行编码字典 */
export function fetchFiatPayoutBankCodes() {
  return apiFetch<ListFiatPayoutBankCodesDto>('/wallet/payout-accounts/fiat/bank-codes')
}

/** GET /api/v1/wallet/payout-accounts/fiat/ewallet-providers — 数字钱包品牌字典 */
export function fetchFiatPayoutEwalletProviders() {
  return apiFetch<ListFiatPayoutEwalletProvidersDto>(
    '/wallet/payout-accounts/fiat/ewallet-providers',
  )
}

/** GET /api/v1/wallet/payout-accounts/fiat/countries — 银行卡国家/地区字典 */
export function fetchFiatPayoutCountries() {
  return apiFetch<ListFiatPayoutCountriesDto>('/wallet/payout-accounts/fiat/countries')
}

/** GET /api/v1/wallet/payout-accounts/crypto — 加密提现地址列表 */
export function fetchCryptoPayoutAddresses() {
  return apiFetch<ListCryptoPayoutAddressesDto>('/wallet/payout-accounts/crypto')
}

/** POST /api/v1/wallet/payout-accounts/crypto — 新增加密提现地址 */
export function createCryptoPayoutAddress(payload: CreateCryptoPayoutAddressDto) {
  return apiFetch<CryptoPayoutAddressDto>('/wallet/payout-accounts/crypto', {
    method: 'POST',
    body: payload,
  })
}

/** PATCH /api/v1/wallet/payout-accounts/crypto/{id} — 编辑加密提现地址 */
export function updateCryptoPayoutAddress(id: number, payload: UpdateCryptoPayoutAddressDto) {
  return apiFetch<CryptoPayoutAddressDto>(`/wallet/payout-accounts/crypto/${id}`, {
    method: 'PATCH',
    body: payload,
  })
}

/** DELETE /api/v1/wallet/payout-accounts/crypto/{id} */
export function deleteCryptoPayoutAddress(id: number, verificationId?: string) {
  return apiFetch(`/wallet/payout-accounts/crypto/${id}`, {
    method: 'DELETE',
    body: authBody('verification_id', verificationId),
  })
}

/** POST /api/v1/wallet/payout-accounts/crypto/{id}/default */
export function setDefaultCryptoPayoutAddress(id: number, verificationId?: string) {
  return apiFetch(`/wallet/payout-accounts/crypto/${id}/default`, {
    method: 'POST',
    body: authBody('verification_id', verificationId),
  })
}

/** GET /api/v1/wallet/venues — 额度转换场馆列表 */
export function fetchVenueList(categoryCode?: string) {
  return apiFetch<VenueListDto>('/wallet/venues', {
    query: categoryCode ? { category_code: categoryCode } : undefined,
  })
}

/** PUT /api/v1/wallet/venues/settings — 更新自动额度转换开关（幂等） */
export function updateVenueSettings(payload: UpdateVenueSettingsDto) {
  return apiFetch<VenueSettingsDto>('/wallet/venues/settings', {
    method: 'PUT',
    body: payload,
  })
}

/** POST /wallet/venues/{vendor_code}/deposit|recover — 中心钱包 ⇄ 场馆 */
export function transferVenue(
  vendorCode: string,
  direction: 'in' | 'out',
  payload: VenueTransferRequestDto,
) {
  const action = direction === 'in' ? 'deposit' : 'recover'
  return apiFetch<VenueTransferResponseDto>(
    `/wallet/venues/${encodeURIComponent(vendorCode)}/${action}`,
    { method: 'POST', body: payload },
  )
}

/** POST /api/v1/wallet/venues/recover-all — 余额找回（一键扫描并收回全部场馆） */
export function recoverAllVenues() {
  return apiFetch<RecoverAllVenuesResponseDto>('/wallet/venues/recover-all', {
    method: 'POST',
    body: { client_order_id: createUuid() },
  })
}
